// TypeScript port of the legacy C# GetXmlData() field extraction.
// All values are kept as raw strings (untrimmed types preserved), matching the
// original ValueTable — no numeric parsing, same as the C# InnerText reads.

export interface MachineData {
  Machine?: string;
  Version?: string;
  UniqueID?: string;
  DateTime?: string;

  SphereOD?: string;
  SphereOS?: string;
  CylinderR?: string;
  CylinderL?: string;
  AxisR?: string;
  AxisL?: string;
  AcuityR?: string;
  AcuityL?: string;
  EAR?: string;
  EAL?: string;

  Kera1R?: string; Kera2R?: string; Kera3R?: string; Kera4R?: string; Kera5R?: string;
  Kera6R?: string; Kera7R?: string; Kera8R?: string; Kera9R?: string;
  Kera1L?: string; Kera2L?: string; Kera3L?: string; Kera4L?: string; Kera5L?: string;
  Kera6L?: string; Kera7L?: string; Kera8L?: string; Kera9L?: string;

  MIMR?: string;
  MIML?: string;
  CIMR?: string;
  CIML?: string;
  TR?: string;
  TL?: string;
  
  HabitualSphereR?: string; HabitualCylinderR?: string; HabitualAxisR?: string;
  HabitualSphereL?: string; HabitualCylinderL?: string; HabitualAxisL?: string;
  HabitualAddR?: string; HabitualAcuityR?: string;
  HabitualAddL?: string; HabitualAcuityL?: string;
  HabitualPrismHR?: string; HabitualPrismVR?: string;
  HabitualPrismHL?: string; HabitualPrismVL?: string;

  PachymetryR1?: string; PachymetryR2?: string;
  PachymetryL1?: string; PachymetryL2?: string;
}

// Namespaced tag names (e.g. "nsREF:R") are unique within the document, so a
// simple scoped getElementsByTagName chain is a safe stand-in for the
// original absolute/relative XPath lookups (no namespace resolver needed).
function getText(root: Element | XMLDocument, path: string[]): string | undefined {
  let node: Element | XMLDocument | null = root;
  for (const tag of path) {
    if (!node) return undefined;
    node = node.getElementsByTagName(tag)[0] ?? null;
  }
  return node ? (node.textContent?.trim() || undefined) : undefined;
}

export function getXmlData(xml: string): MachineData {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('Invalid XML');
  }

  const VT: MachineData = {};

  VT.Version = getText(doc, ['nsCommon:Version']);
  const company = getText(doc, ['nsCommon:Company']);
  const model = getText(doc, ['nsCommon:ModelName']);
  VT.Machine = company && model ? `${company} ${model}` : company ?? model;

  VT.SphereOD = getText(doc, ['nsREF:R', 'nsREF:Median', 'nsREF:Sphere']);
  VT.CylinderR = getText(doc, ['nsREF:R', 'nsREF:Median', 'nsREF:Cylinder']);
  VT.AxisR = getText(doc, ['nsREF:R', 'nsREF:Median', 'nsREF:Axis']);
  VT.AcuityR = getText(doc, ['nsREF:R', 'nsREF:Median', 'nsREF:SE']);
  VT.SphereOS = getText(doc, ['nsREF:L', 'nsREF:Median', 'nsREF:Sphere']);
  VT.CylinderL = getText(doc, ['nsREF:L', 'nsREF:Median', 'nsREF:Cylinder']);
  VT.AxisL = getText(doc, ['nsREF:L', 'nsREF:Median', 'nsREF:Axis']);
  VT.AcuityL = getText(doc, ['nsREF:L', 'nsREF:Median', 'nsREF:SE']);

  VT.Kera1R = getText(doc, ['nsKM:R', 'nsKM:Median', 'nsKM:R1', 'nsKM:Radius']);
  VT.Kera2R = getText(doc, ['nsKM:R', 'nsKM:Median', 'nsKM:R1', 'nsKM:Power']);
  VT.Kera3R = getText(doc, ['nsKM:R', 'nsKM:Median', 'nsKM:R1', 'nsKM:Axis']);
  VT.Kera4R = getText(doc, ['nsKM:R', 'nsKM:Median', 'nsKM:R2', 'nsKM:Radius']);
  VT.Kera5R = getText(doc, ['nsKM:R', 'nsKM:Median', 'nsKM:R2', 'nsKM:Power']);
  VT.Kera9R = getText(doc, ['nsKM:R', 'nsKM:Median', 'nsKM:R2', 'nsKM:Axis']);
  VT.Kera1L = getText(doc, ['nsKM:L', 'nsKM:Median', 'nsKM:R1', 'nsKM:Radius']);
  VT.Kera2L = getText(doc, ['nsKM:L', 'nsKM:Median', 'nsKM:R1', 'nsKM:Power']);
  VT.Kera3L = getText(doc, ['nsKM:L', 'nsKM:Median', 'nsKM:R1', 'nsKM:Axis']);
  VT.Kera4L = getText(doc, ['nsKM:L', 'nsKM:Median', 'nsKM:R2', 'nsKM:Radius']);
  VT.Kera5L = getText(doc, ['nsKM:L', 'nsKM:Median', 'nsKM:R2', 'nsKM:Power']);
  VT.Kera9L = getText(doc, ['nsKM:L', 'nsKM:Median', 'nsKM:R2', 'nsKM:Axis']);
  // Kera6/7/8 (R & L) are never populated in the source routine either.

  VT.MIMR = getText(doc, ['nsTM:Measure', 'nsTM:TM', 'nsTM:R', 'nsTM:Average', 'nsTM:IOP_mmHg']);
  VT.MIML = getText(doc, ['nsTM:Measure', 'nsTM:TM', 'nsTM:L', 'nsTM:Average', 'nsTM:IOP_mmHg']);
  VT.CIMR = getText(doc, ['nsTM:Measure', 'nsTM:CorrectedIOP', 'nsTM:Formula1', 'nsTM:R', 'nsTM:Corrected', 'nsTM:IOP_mmHg']);
  VT.CIML = getText(doc, ['nsTM:Measure', 'nsTM:CorrectedIOP', 'nsTM:Formula1', 'nsTM:L', 'nsTM:Corrected', 'nsTM:IOP_mmHg']);

  if (VT.Version === '1.2') {
    VT.PachymetryR1 = getText(doc, ['nsTM:Measure', 'nsTM:CorrectedIOP', 'nsTM:Formula1', 'nsTM:R', 'nsTM:CCT']);
    VT.PachymetryR2 = getText(doc, ['nsTM:Measure', 'nsTM:CorrectedIOP', 'nsTM:Formula1', 'nsTM:R', 'nsTM:Corrected', 'nsTM:IOP_mmHg']);
    VT.PachymetryL1 = getText(doc, ['nsTM:Measure', 'nsTM:CorrectedIOP', 'nsTM:Formula1', 'nsTM:L', 'nsTM:CCT']);
    VT.PachymetryL2 = getText(doc, ['nsTM:Measure', 'nsTM:CorrectedIOP', 'nsTM:Formula1', 'nsTM:L', 'nsTM:Corrected', 'nsTM:IOP_mmHg']);
  } else if (VT.Version === '1.5') {
    VT.PachymetryR1 = getText(doc, ['CCT', 'R', 'List', 'CCT_mm']);
    VT.PachymetryL1 = getText(doc, ['CCT', 'L', 'List', 'CCT_mm']);
  }

  VT.TR = '';
  VT.TL = '';

  VT.HabitualSphereR = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:R', 'nsLM:Sphere']);
  VT.HabitualCylinderR = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:R', 'nsLM:Cylinder']);
  VT.HabitualAxisR = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:R', 'nsLM:Axis']);
  VT.HabitualAddR = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:R', 'nsLM:Add1']);
  VT.HabitualAcuityR = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:R', 'nsLM:Add2']);
  VT.HabitualPrismHR = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:R', 'nsLM:H']);
  VT.HabitualPrismVR = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:R', 'nsLM:V']);

  VT.HabitualSphereL = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:L', 'nsLM:Sphere']);
  VT.HabitualCylinderL = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:L', 'nsLM:Cylinder']);
  VT.HabitualAxisL = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:L', 'nsLM:Axis']);
  VT.HabitualAddL = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:L', 'nsLM:Add1']);
  VT.HabitualAcuityL = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:L', 'nsLM:Add2']);
  // NOTE: original C# reads Prism H/V for the L eye from the R node too
  // (copy-paste bug in the source) — reproduced as-is below.
  VT.HabitualPrismHL = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:R', 'nsLM:H']);
  VT.HabitualPrismVL = getText(doc, ['nsLM:Measure', 'nsLM:LM', 'nsLM:R', 'nsLM:V']);

  return VT;
}